import * as React from "react";
import { useEffect, useRef } from "react";
import { MapleEngine } from "../../engine-core/src";

export type GameCleanup = void | (() => void);

export interface GameHostProps {
  appId: string;
  createGame: (engine: MapleEngine) => GameCleanup | Promise<GameCleanup>;
  targetFPS?: number;
  className?: string;
  renderMode?: '2d' | 'webgl';
}

export const MapleGameHost: React.FC<GameHostProps> = ({ appId, createGame, targetFPS = 60, className, renderMode = '2d' }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const canvasRef = useRef<HTMLCanvasElement | null>(null);
  const engineRef = useRef<MapleEngine | null>(null);

  const canvasKey = `${appId}-${renderMode}`;

  const resizeCanvas = () => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const dpr = Math.max(1, window.devicePixelRatio || 1);
    const rect = canvas.getBoundingClientRect();
    const width = Math.max(1, Math.floor(rect.width * dpr));
    const height = Math.max(1, Math.floor(rect.height * dpr));

    if (canvas.width !== width || canvas.height !== height) {
      canvas.width = width;
      canvas.height = height;
    }
  };

  useEffect(() => {
    const canvasEl = canvasRef.current;
    if (!canvasEl) return;

    let mounted = true;
    let cleanupGame: GameCleanup;

    canvasEl.width = 0;
    canvasEl.height = 0;

    resizeCanvas();

    if (renderMode === '2d') {
      const ctx = canvasEl.getContext("2d");
      if (ctx) {
        ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
      }
    }

    const engine = new MapleEngine({ canvas: canvasEl, appId, targetFPS });
    engineRef.current = engine;

    const start = async () => {
      cleanupGame = await Promise.resolve(createGame(engine));
      if (!mounted) {
        if (typeof cleanupGame === "function") {
          cleanupGame();
        }
        return;
      }
      engine.start();
    };

    start();

    const handleResize = () => {
      resizeCanvas();
    };

    window.addEventListener("resize", handleResize);

    const observer = new ResizeObserver(() => {
      resizeCanvas();
    });
    if (containerRef.current) {
      observer.observe(containerRef.current);
    }

    const mediaQuery = window.matchMedia(`(resolution: ${window.devicePixelRatio}dppx)`);
    const dprListener = () => resizeCanvas();
    if (mediaQuery && "addEventListener" in mediaQuery) {
      mediaQuery.addEventListener("change", dprListener);
    }

    return () => {
      mounted = false;
      window.removeEventListener("resize", handleResize);
      if (mediaQuery && "removeEventListener" in mediaQuery) {
        mediaQuery.removeEventListener("change", dprListener);
      }
      observer.disconnect();

      if (typeof cleanupGame === "function") {
        cleanupGame();
      }

      if (engineRef.current) {
        engineRef.current.stop();
        engineRef.current = null;
      }

      if (canvasEl) {
        if (renderMode === '2d') {
          const ctx = canvasEl.getContext("2d");
          if (ctx) {
            ctx.clearRect(0, 0, canvasEl.width, canvasEl.height);
          }
        }
        canvasEl.width = 0;
        canvasEl.height = 0;
      }
    };
  }, [appId, targetFPS, createGame, renderMode]);

  return (
    <div ref={containerRef} className={`w-full h-full ${className ?? ""}`.trim()}>
      <canvas key={canvasKey} ref={canvasRef} className="w-full h-full block" />
    </div>
  );
};